import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Shield, CheckCircle2, ArrowLeft, KeyRound, Lock, ChevronDown } from "lucide-react";
import { API, LOJACK_LOGO } from "../config";

// Self-update i18n labels — mirrors the backend pack at
// services/customer_self_update_i18n.py. Only IT/UK/FR/ES/DE/PL are
// native; everything else falls back to UK English (same policy
// already used across the wallet flow).
const I18N = {
  IT: {
    title: "Aggiorna i tuoi dati",
    subtitle: "Verifica identità tramite codice email",
    intro_text: "Per modificare i tuoi dati personali, ti invieremo un codice di verifica all'indirizzo email registrato.",
    request_otp_btn: "Invia codice di verifica",
    otp_label: "Inserisci il codice ricevuto via email",
    otp_placeholder: "6 cifre",
    verify_btn: "Verifica codice",
    resend_btn: "Reinvia codice",
    expired_msg: "Codice scaduto. Richiedi un nuovo codice.",
    invalid_msg: "Codice non valido. Riprova.",
    rate_limit_msg: "Troppe richieste. Riprova tra un'ora.",
    email_sent_to: "Codice inviato a",
    edit_intro: "Modifica i campi sotto. Quelli grigi sono bloccati dal contratto firmato.",
    locked_field_hint: "Bloccato dal contratto firmato",
    save_btn: "Salva modifiche",
    saving: "Salvataggio…",
    success_title: "Dati aggiornati con successo",
    success_body: "Le modifiche sono state registrate. Hai ricevuto una conferma via email e anche il tuo concessionario è stato avvisato.",
    back_to_wallet: "Torna alla tua card",
    no_changes_msg: "Nessuna modifica effettuata.",
    f_address: "Indirizzo",
    f_civic_number: "Numero civico",
    f_city: "Città",
    f_province: "Provincia",
    f_zip_code: "CAP",
    f_phone2: "Telefono aggiuntivo",
    f_phone2_prefix: "Prefisso aggiuntivo",
    f_phone1: "Cellulare 1",
    f_email: "Email",
    f_first_name: "Nome",
    f_last_name: "Cognome",
    f_company: "Ragione Sociale",
    f_tax_code: "Codice Fiscale",
    f_vat: "P.IVA",
    request_change_label: "Richiedi modifica al concessionario",
    request_change_hint: "Il tuo concessionario riceverà la richiesta e potrà approvarla o rifiutarla.",
    request_change_placeholder: "Nuovo valore",
    request_change_note_placeholder: "Motivazione (facoltativa)",
    request_change_submit: "Invia richiesta",
    request_change_cancel: "Annulla",
    request_change_sending: "Invio in corso…",
    request_change_success: "Richiesta inviata al concessionario",
    request_change_error: "Errore nell'invio della richiesta",
  },
  UK: {
    title: "Update your details",
    subtitle: "Identity verification via email code",
    intro_text: "To modify your personal data, we will send a verification code to your registered email address.",
    request_otp_btn: "Send verification code",
    otp_label: "Enter the code you received by email",
    otp_placeholder: "6 digits",
    verify_btn: "Verify code",
    resend_btn: "Resend code",
    expired_msg: "Code expired. Please request a new one.",
    invalid_msg: "Invalid code. Try again.",
    rate_limit_msg: "Too many requests. Try again in an hour.",
    email_sent_to: "Code sent to",
    edit_intro: "Edit the fields below. Greyed-out fields are locked by the signed contract.",
    locked_field_hint: "Locked by the signed contract",
    save_btn: "Save changes",
    saving: "Saving…",
    success_title: "Details updated successfully",
    success_body: "Your changes have been saved. You have received a confirmation by email and your dealer has been notified.",
    back_to_wallet: "Back to your card",
    no_changes_msg: "No changes made.",
    f_address: "Address",
    f_civic_number: "Street number",
    f_city: "City",
    f_province: "Province",
    f_zip_code: "Postal code",
    f_phone2: "Additional phone",
    f_phone2_prefix: "Additional prefix",
    f_phone1: "Mobile phone",
    f_email: "Email",
    f_first_name: "First name",
    f_last_name: "Last name",
    f_company: "Company name",
    f_tax_code: "Tax code",
    f_vat: "VAT number",
    request_change_label: "Request change from your dealer",
    request_change_hint: "Your dealer will review the request and either approve or reject it.",
    request_change_placeholder: "New value",
    request_change_note_placeholder: "Reason (optional)",
    request_change_submit: "Send request",
    request_change_cancel: "Cancel",
    request_change_sending: "Sending…",
    request_change_success: "Request sent to your dealer",
    request_change_error: "Failed to send the request",
  },
  FR: {
    title: "Mettre à jour vos données",
    subtitle: "Vérification d'identité par code e-mail",
    intro_text: "Pour modifier vos données personnelles, nous enverrons un code de vérification à votre adresse e-mail enregistrée.",
    request_otp_btn: "Envoyer le code",
    otp_label: "Saisissez le code reçu par e-mail",
    otp_placeholder: "6 chiffres",
    verify_btn: "Vérifier le code",
    resend_btn: "Renvoyer le code",
    expired_msg: "Code expiré. Demandez un nouveau code.",
    invalid_msg: "Code invalide. Réessayez.",
    rate_limit_msg: "Trop de demandes. Réessayez dans une heure.",
    email_sent_to: "Code envoyé à",
    edit_intro: "Modifiez les champs ci-dessous. Les champs grisés sont verrouillés par le contrat signé.",
    locked_field_hint: "Verrouillé par le contrat signé",
    save_btn: "Enregistrer",
    saving: "Enregistrement…",
    success_title: "Données mises à jour",
    success_body: "Vos modifications ont été enregistrées. Vous avez reçu une confirmation par e-mail et votre concessionnaire a été notifié.",
    back_to_wallet: "Retour à votre carte",
    no_changes_msg: "Aucune modification effectuée.",
    f_address: "Adresse",
    f_civic_number: "Numéro",
    f_city: "Ville",
    f_province: "Département",
    f_zip_code: "Code postal",
    f_phone2: "Téléphone supplémentaire",
    f_phone2_prefix: "Indicatif supplémentaire",
    f_email: "E-mail",
    f_first_name: "Prénom",
    f_last_name: "Nom",
    f_company: "Raison sociale",
    f_tax_code: "Code fiscal",
    f_vat: "TVA",
  },
  ES: {
    title: "Actualizar tus datos",
    subtitle: "Verificación de identidad por código de correo",
    intro_text: "Para modificar tus datos personales, te enviaremos un código de verificación a tu correo registrado.",
    request_otp_btn: "Enviar código",
    otp_label: "Introduce el código recibido por correo",
    otp_placeholder: "6 dígitos",
    verify_btn: "Verificar código",
    resend_btn: "Reenviar código",
    expired_msg: "Código caducado. Solicita uno nuevo.",
    invalid_msg: "Código no válido. Inténtalo de nuevo.",
    rate_limit_msg: "Demasiadas solicitudes. Inténtalo en una hora.",
    email_sent_to: "Código enviado a",
    edit_intro: "Edita los campos a continuación. Los campos en gris están bloqueados por el contrato firmado.",
    locked_field_hint: "Bloqueado por el contrato firmado",
    save_btn: "Guardar cambios",
    saving: "Guardando…",
    success_title: "Datos actualizados con éxito",
    success_body: "Tus cambios se han registrado. Has recibido una confirmación por correo y tu concesionario ha sido notificado.",
    back_to_wallet: "Volver a tu tarjeta",
    no_changes_msg: "No se han realizado cambios.",
    f_address: "Dirección",
    f_civic_number: "Número",
    f_city: "Ciudad",
    f_province: "Provincia",
    f_zip_code: "Código postal",
    f_phone2: "Teléfono adicional",
    f_phone2_prefix: "Prefijo adicional",
    f_email: "Correo",
    f_first_name: "Nombre",
    f_last_name: "Apellido",
    f_company: "Razón social",
    f_tax_code: "Código fiscal",
    f_vat: "NIF",
  },
  DE: {
    title: "Daten aktualisieren",
    subtitle: "Identitätsprüfung per E-Mail-Code",
    intro_text: "Um Ihre persönlichen Daten zu ändern, senden wir einen Bestätigungscode an Ihre registrierte E-Mail-Adresse.",
    request_otp_btn: "Code senden",
    otp_label: "Geben Sie den per E-Mail erhaltenen Code ein",
    otp_placeholder: "6 Ziffern",
    verify_btn: "Code prüfen",
    resend_btn: "Code erneut senden",
    expired_msg: "Code abgelaufen. Neuen Code anfordern.",
    invalid_msg: "Ungültiger Code. Bitte erneut versuchen.",
    rate_limit_msg: "Zu viele Anfragen. In einer Stunde erneut versuchen.",
    email_sent_to: "Code gesendet an",
    edit_intro: "Bearbeiten Sie die Felder unten. Ausgegraute Felder sind durch den unterzeichneten Vertrag gesperrt.",
    locked_field_hint: "Durch unterzeichneten Vertrag gesperrt",
    save_btn: "Änderungen speichern",
    saving: "Wird gespeichert…",
    success_title: "Daten erfolgreich aktualisiert",
    success_body: "Ihre Änderungen wurden gespeichert. Sie haben eine Bestätigung per E-Mail erhalten und Ihr Händler wurde benachrichtigt.",
    back_to_wallet: "Zurück zur Karte",
    no_changes_msg: "Keine Änderungen vorgenommen.",
    f_address: "Adresse",
    f_civic_number: "Hausnummer",
    f_city: "Stadt",
    f_province: "Bundesland",
    f_zip_code: "Postleitzahl",
    f_phone2: "Zusätzliche Telefonnummer",
    f_phone2_prefix: "Zusätzliche Vorwahl",
    f_email: "E-Mail",
    f_first_name: "Vorname",
    f_last_name: "Nachname",
    f_company: "Firmenname",
    f_tax_code: "Steuernummer",
    f_vat: "USt-IdNr.",
  },
  PL: {
    title: "Zaktualizuj swoje dane",
    subtitle: "Weryfikacja tożsamości kodem e-mail",
    intro_text: "Aby zmienić dane osobowe, wyślemy kod weryfikacyjny na Twój zarejestrowany adres e-mail.",
    request_otp_btn: "Wyślij kod",
    otp_label: "Wpisz kod otrzymany e-mailem",
    otp_placeholder: "6 cyfr",
    verify_btn: "Zweryfikuj kod",
    resend_btn: "Wyślij ponownie",
    expired_msg: "Kod wygasł. Poproś o nowy.",
    invalid_msg: "Nieprawidłowy kod. Spróbuj ponownie.",
    rate_limit_msg: "Zbyt wiele żądań. Spróbuj za godzinę.",
    email_sent_to: "Kod wysłany na",
    edit_intro: "Edytuj pola poniżej. Wyszarzone pola są zablokowane przez podpisaną umowę.",
    locked_field_hint: "Zablokowane przez podpisaną umowę",
    save_btn: "Zapisz zmiany",
    saving: "Zapisywanie…",
    success_title: "Dane zaktualizowane",
    success_body: "Twoje zmiany zostały zapisane. Otrzymałeś potwierdzenie e-mailem, a dealer został powiadomiony.",
    back_to_wallet: "Powrót do karty",
    no_changes_msg: "Brak zmian.",
    f_address: "Adres",
    f_civic_number: "Numer",
    f_city: "Miasto",
    f_province: "Województwo",
    f_zip_code: "Kod pocztowy",
    f_phone2: "Dodatkowy telefon",
    f_phone2_prefix: "Dodatkowy prefiks",
    f_email: "E-mail",
    f_first_name: "Imię",
    f_last_name: "Nazwisko",
    f_company: "Nazwa firmy",
    f_tax_code: "NIP",
    f_vat: "VAT",
  },
  PT: {
    title: "Atualizar os seus dados",
    subtitle: "Verificação de identidade por código de e-mail",
    intro_text: "Para modificar os seus dados pessoais, enviaremos um código de verificação para o seu e-mail registado.",
    request_otp_btn: "Enviar código",
    otp_label: "Introduza o código recebido por e-mail",
    otp_placeholder: "6 dígitos",
    verify_btn: "Verificar código",
    resend_btn: "Reenviar código",
    expired_msg: "Código expirado. Solicite um novo.",
    invalid_msg: "Código inválido. Tente novamente.",
    rate_limit_msg: "Demasiados pedidos. Tente novamente daqui a uma hora.",
    email_sent_to: "Código enviado para",
    edit_intro: "Edite os campos abaixo. Os campos a cinzento estão bloqueados pelo contrato assinado.",
    locked_field_hint: "Bloqueado pelo contrato assinado",
    save_btn: "Guardar alterações",
    saving: "A guardar…",
    success_title: "Dados atualizados com sucesso",
    success_body: "As suas alterações foram guardadas. Recebeu uma confirmação por e-mail e o seu concessionário foi notificado.",
    back_to_wallet: "Voltar ao seu cartão",
    no_changes_msg: "Nenhuma alteração efetuada.",
    f_address: "Morada",
    f_civic_number: "Número",
    f_city: "Cidade",
    f_province: "Distrito",
    f_zip_code: "Código postal",
    f_phone2: "Telefone adicional",
    f_phone2_prefix: "Indicativo adicional",
    f_email: "E-mail",
    f_first_name: "Nome",
    f_last_name: "Apelido",
    f_company: "Razão social",
    f_tax_code: "NIF",
    f_vat: "IVA",
  },
  NL: {
    title: "Gegevens bijwerken",
    subtitle: "Identiteitsverificatie via e-mailcode",
    intro_text: "Om uw persoonlijke gegevens te wijzigen sturen wij een verificatiecode naar uw geregistreerde e-mailadres.",
    request_otp_btn: "Code verzenden",
    otp_label: "Voer de via e-mail ontvangen code in",
    otp_placeholder: "6 cijfers",
    verify_btn: "Code verifiëren",
    resend_btn: "Code opnieuw verzenden",
    expired_msg: "Code verlopen. Vraag een nieuwe aan.",
    invalid_msg: "Ongeldige code. Probeer opnieuw.",
    rate_limit_msg: "Te veel verzoeken. Probeer over een uur opnieuw.",
    email_sent_to: "Code verzonden naar",
    edit_intro: "Bewerk de velden hieronder. Grijs gemaakte velden zijn vergrendeld door het ondertekende contract.",
    locked_field_hint: "Vergrendeld door het ondertekende contract",
    save_btn: "Wijzigingen opslaan",
    saving: "Opslaan…",
    success_title: "Gegevens succesvol bijgewerkt",
    success_body: "Uw wijzigingen zijn opgeslagen. U heeft een bevestiging per e-mail ontvangen en uw dealer is op de hoogte gebracht.",
    back_to_wallet: "Terug naar uw kaart",
    no_changes_msg: "Geen wijzigingen aangebracht.",
    f_address: "Adres",
    f_civic_number: "Huisnummer",
    f_city: "Stad",
    f_province: "Provincie",
    f_zip_code: "Postcode",
    f_phone2: "Extra telefoonnummer",
    f_phone2_prefix: "Extra netnummer",
    f_email: "E-mail",
    f_first_name: "Voornaam",
    f_last_name: "Achternaam",
    f_company: "Bedrijfsnaam",
    f_tax_code: "Fiscaal nummer",
    f_vat: "Btw-nummer",
  },
};

const _ALIASES = {
  CH: "DE", AT: "DE",
  BE: "FR", LU: "FR",
  GBZ: "UK", IRL: "UK",
  SE: "UK", NO: "UK",
  FI: "UK", DK: "UK", CZ: "UK", GR: "UK",
  HU: "UK", RO: "UK", CY: "UK",
};

// Native languages exposed in the picker dropdown. Order matters —
// most-spoken markets first. Flag emojis are decorative; the actual
// label uses the language's own native name so the customer can
// recognise their language even if they don't read English/Italian.
const LANGUAGE_OPTIONS = [
  { code: "IT", label: "Italiano", flag: "🇮🇹" },
  { code: "UK", label: "English", flag: "🇬🇧" },
  { code: "FR", label: "Français", flag: "🇫🇷" },
  { code: "ES", label: "Español", flag: "🇪🇸" },
  { code: "DE", label: "Deutsch", flag: "🇩🇪" },
  { code: "PL", label: "Polski", flag: "🇵🇱" },
  { code: "PT", label: "Português", flag: "🇵🇹" },
  { code: "NL", label: "Nederlands", flag: "🇳🇱" },
];

// Resolve the language CODE (not the pack) from a country — used to
// pick the default selection in the language dropdown.
const getDefaultLangCode = (country) => {
  const code = (country || "").toUpperCase();
  if (_ALIASES[code]) return _ALIASES[code];
  if (I18N[code]) return code;
  return "UK";
};

const getLang = (country) => {
  const code = (country || "").toUpperCase();
  if (_ALIASES[code]) return I18N[_ALIASES[code]];
  if (I18N[code]) return I18N[code];
  return I18N.UK;
};

export default function CustomerSelfUpdatePage() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState("loading"); // loading | request_otp | enter_otp | edit | success
  const [customer, setCustomer] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState(null);
  const [form, setForm] = useState({});
  const [changes, setChanges] = useState({});
  // Zorzutti CR 29/07/2026 — inline change-request flow for locked
  // identity fields (email / phone / tax_code). Only one at a time.
  const [changeReqField, setChangeReqField] = useState(null); // "email"|"phone"|"tax_code"|null
  const [changeReqValue, setChangeReqValue] = useState("");
  const [changeReqNote, setChangeReqNote] = useState("");
  const [changeReqBusy, setChangeReqBusy] = useState(false);
  // Customer-pickable UI language. Defaults to the country-derived
  // pack code on first load (set inside the useEffect below); the
  // customer can override it via the dropdown in the top-right.
  const [activeLang, setActiveLang] = useState("UK");

  const t = I18N[activeLang] || I18N.UK;
  const isBusiness = customer?.customer_type === "business";
  const isSigned = !!customer?.contract_signed;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/customers/${customerId}`);
        setCustomer(res.data);
        setActiveLang(getDefaultLangCode(res.data.country));
        setForm({
          address: res.data.address || "",
          civic_number: res.data.civic_number || "",
          city: res.data.city || "",
          province: res.data.province || "",
          zip_code: res.data.zip_code || "",
          phone2: res.data.phone2 || "",
          phone2_prefix: res.data.phone2_prefix || "",
        });
        setStep("request_otp");
      } catch (e) {
        toast.error("Customer non trovato");
        setStep("error");
      }
    })();
  }, [customerId]);

  const handleRequestOtp = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/customers/${customerId}/self-update/request-otp`, {
        ui_language: activeLang,
      });
      setMaskedEmail(res.data.email_masked);
      setStep("enter_otp");
      toast.success(`${t.email_sent_to} ${res.data.email_masked}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      if (status === 429) toast.error(t.rate_limit_msg);
      else toast.error(detail || "Errore invio codice");
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      toast.error(t.invalid_msg);
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/customers/${customerId}/self-update/verify-otp`, {
        otp_code: otpCode,
      });
      setToken(res.data.token);
      setStep("edit");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("scaduto")) toast.error(t.expired_msg);
      else toast.error(detail || t.invalid_msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/customers/${customerId}/self-update`, {
        token,
        ui_language: activeLang,
        ...form,
      });
      setChanges(res.data.changes || {});
      setStep("success");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail || "Errore salvataggio");
      if (e?.response?.status === 401) {
        // Token expired — restart from OTP step
        setStep("request_otp");
        setOtpCode("");
        setToken(null);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitChangeRequest = async () => {
    if (!token) {
      toast.error(t.expired_msg);
      return;
    }
    const value = (changeReqValue || "").trim();
    if (!value) return;
    setChangeReqBusy(true);
    try {
      await axios.post(`${API}/customers/${customerId}/self-update/request-change`, {
        token,
        field: changeReqField,
        new_value: value,
        note: changeReqNote || null,
        ui_language: activeLang,
      });
      toast.success(t.request_change_success);
      setChangeReqField(null);
      setChangeReqValue("");
      setChangeReqNote("");
      // The token was single-use — force the customer to re-verify
      // if they want to submit another request or edit other fields.
      setToken(null);
      setStep("request_otp");
      setOtpCode("");
    } catch (e) {
      const detail = e?.response?.data?.detail || t.request_change_error;
      toast.error(typeof detail === "string" ? detail : t.request_change_error);
      if (e?.response?.status === 401) {
        setStep("request_otp");
        setOtpCode("");
        setToken(null);
      }
    } finally {
      setChangeReqBusy(false);
    }
  };

  const lockedField = (label, value, requestKey = null) => {
    const isEditingThis = changeReqField === requestKey && requestKey;
    return (
      <div className="flex flex-col gap-1" data-testid={`locked-field-${requestKey || label.toLowerCase()}`}>
        <label className="text-gray-500 text-xs flex items-center gap-1.5">
          <Lock size={11} className="text-gray-600" />
          {label}
          <span className="text-[10px] text-gray-600 ml-auto italic">{t.locked_field_hint}</span>
        </label>
        <input
          value={value || ""}
          disabled
          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-500 cursor-not-allowed text-sm"
        />
        {requestKey && !isEditingThis && (
          <button
            type="button"
            onClick={() => {
              setChangeReqField(requestKey);
              setChangeReqValue("");
              setChangeReqNote("");
            }}
            className="self-start text-[11px] text-[#2B648F] hover:text-[#3B7FAF] underline underline-offset-2 mt-1"
            data-testid={`request-change-btn-${requestKey}`}
          >
            {t.request_change_label}
          </button>
        )}
        {isEditingThis && (
          <div className="mt-2 p-3 bg-[#0F0F0F] border border-[#2B648F]/40 rounded-lg space-y-2" data-testid={`request-change-panel-${requestKey}`}>
            <p className="text-[10px] text-gray-400 leading-relaxed">{t.request_change_hint}</p>
            <input
              type="text"
              value={changeReqValue}
              onChange={(e) => setChangeReqValue(e.target.value)}
              placeholder={t.request_change_placeholder}
              className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-[#2B648F] focus:outline-none"
              data-testid={`request-change-value-${requestKey}`}
              autoFocus
            />
            <textarea
              value={changeReqNote}
              onChange={(e) => setChangeReqNote(e.target.value)}
              placeholder={t.request_change_note_placeholder}
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white text-sm focus:border-[#2B648F] focus:outline-none resize-none"
              data-testid={`request-change-note-${requestKey}`}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={submitChangeRequest}
                disabled={changeReqBusy || !changeReqValue.trim()}
                className="flex-1 bg-[#2B648F] hover:bg-[#3B7FAF] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-2 rounded-lg transition-colors"
                data-testid={`request-change-submit-${requestKey}`}
              >
                {changeReqBusy ? t.request_change_sending : t.request_change_submit}
              </button>
              <button
                type="button"
                onClick={() => {
                  setChangeReqField(null);
                  setChangeReqValue("");
                  setChangeReqNote("");
                }}
                disabled={changeReqBusy}
                className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg transition-colors"
                data-testid={`request-change-cancel-${requestKey}`}
              >
                {t.request_change_cancel}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const editableField = (label, key, type = "text") => (
    <div className="flex flex-col gap-1">
      <label className="text-gray-400 text-xs">{label}</label>
      <input
        type={type}
        value={form[key] || ""}
        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
        className="w-full px-3 py-2 bg-[#0F0F0F] border border-gray-700 rounded-lg text-white text-sm focus:border-[#2B648F] focus:outline-none transition-colors"
        data-testid={`self-update-input-${key}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-2xl mx-auto px-5 py-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <img src={LOJACK_LOGO} alt="LoJack" className="h-9" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate" data-testid="self-update-title">{t.title}</h1>
            <p className="text-xs text-gray-500 mt-1 truncate">{t.subtitle}</p>
          </div>
          {/* Language picker — only visible once the customer is loaded
              so the dropdown doesn't pop in with the wrong default. */}
          {customer && (
            <div className="relative shrink-0" data-testid="self-update-lang-picker-wrapper">
              <select
                value={activeLang}
                onChange={(e) => setActiveLang(e.target.value)}
                className="appearance-none bg-[#111] border border-gray-800 hover:border-gray-600 text-white text-sm rounded-lg py-2 pl-3 pr-8 cursor-pointer focus:outline-none focus:border-[#2B648F] transition-colors"
                aria-label="Lingua / Language"
                data-testid="self-update-lang-picker"
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.flag} {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
            </div>
          )}
        </div>

        {/* Loading */}
        {step === "loading" && (
          <div className="text-center py-20 text-gray-500">…</div>
        )}

        {/* Error */}
        {step === "error" && (
          <div className="bg-red-950/30 border border-red-800 rounded-xl p-6 text-center">
            <p className="text-red-300">Cliente non trovato</p>
          </div>
        )}

        {/* Step 1: request OTP */}
        {step === "request_otp" && (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#2B648F]/20 border border-[#2B648F]/30 flex items-center justify-center">
                <Shield size={18} className="text-[#2B648F]" />
              </div>
              <div>
                <p className="text-sm font-semibold">{customer?.first_name} {customer?.last_name}{customer?.company_name && !customer?.first_name ? customer.company_name : ""}</p>
                <p className="text-xs text-gray-500">{customer?.email}</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">{t.intro_text}</p>
            <button
              onClick={handleRequestOtp}
              disabled={busy}
              className="w-full bg-[#2B648F] hover:bg-[#1E4F73] disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="self-update-request-otp-btn"
            >
              <Mail size={16} />
              {busy ? "…" : t.request_otp_btn}
            </button>
          </div>
        )}

        {/* Step 2: enter OTP */}
        {step === "enter_otp" && (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#2B648F]/20 border border-[#2B648F]/30 flex items-center justify-center">
                <KeyRound size={18} className="text-[#2B648F]" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t.otp_label}</p>
                {maskedEmail && (
                  <p className="text-xs text-gray-500 mt-1">{t.email_sent_to} {maskedEmail}</p>
                )}
              </div>
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder={t.otp_placeholder}
              className="w-full px-4 py-4 bg-[#0F0F0F] border-2 border-gray-700 rounded-xl text-white text-2xl text-center tracking-[12px] font-mono focus:border-[#2B648F] focus:outline-none transition-colors mb-5"
              data-testid="self-update-otp-input"
            />
            <button
              onClick={handleVerifyOtp}
              disabled={busy || otpCode.length !== 6}
              className="w-full bg-[#2B648F] hover:bg-[#1E4F73] disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors mb-3"
              data-testid="self-update-verify-otp-btn"
            >
              {busy ? "…" : t.verify_btn}
            </button>
            <button
              onClick={handleRequestOtp}
              disabled={busy}
              className="w-full text-xs text-gray-500 hover:text-white transition-colors py-2"
              data-testid="self-update-resend-otp-btn"
            >
              {t.resend_btn}
            </button>
          </div>
        )}

        {/* Step 3: edit form */}
        {step === "edit" && (
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-6">
            <p className="text-sm text-gray-400 leading-relaxed mb-6">{t.edit_intro}</p>

            {/* Locked fields (signed contract) — visible but disabled */}
            <div className="space-y-3 mb-5 pb-5 border-b border-gray-800">
              <p className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold flex items-center gap-1.5">
                <Lock size={10} /> {t.locked_field_hint}
              </p>
              {lockedField(t.f_email, customer?.email, "email")}
              {!isBusiness && (
                <>
                  {lockedField(t.f_first_name, customer?.first_name)}
                  {lockedField(t.f_last_name, customer?.last_name)}
                  {customer?.tax_code && lockedField(t.f_tax_code, customer?.tax_code, "tax_code")}
                  {customer?.phone && lockedField(
                    t.f_phone1 || "Cellulare 1",
                    `${customer?.phone_prefix || ''} ${customer?.phone}`.trim(),
                    "phone",
                  )}
                </>
              )}
              {isBusiness && (
                <>
                  {lockedField(t.f_company, customer?.company_name)}
                  {customer?.vat_number && lockedField(t.f_vat, customer?.vat_number)}
                  {customer?.phone && lockedField(
                    t.f_phone1 || "Cellulare 1",
                    `${customer?.phone_prefix || ''} ${customer?.phone}`.trim(),
                    "phone",
                  )}
                </>
              )}
            </div>

            {/* Editable fields */}
            <div className="space-y-3 mb-6">
              {editableField(t.f_address, "address")}
              <div className="grid grid-cols-2 gap-3">
                {editableField(t.f_civic_number, "civic_number")}
                {editableField(t.f_zip_code, "zip_code")}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {editableField(t.f_city, "city")}
                {editableField(t.f_province, "province")}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">{editableField(t.f_phone2_prefix, "phone2_prefix")}</div>
                <div className="col-span-2">{editableField(t.f_phone2, "phone2", "tel")}</div>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={busy}
              className="w-full bg-[#E31837] hover:bg-[#C01230] disabled:opacity-50 text-white font-semibold py-3 rounded-lg transition-colors"
              data-testid="self-update-save-btn"
            >
              {busy ? t.saving : t.save_btn}
            </button>
          </div>
        )}

        {/* Step 4: success */}
        {step === "success" && (
          <div className="bg-[#111] border border-emerald-800 rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={20} className="text-emerald-400" />
              </div>
              <div>
                <p className="font-semibold text-emerald-300" data-testid="self-update-success-title">{t.success_title}</p>
                <p className="text-sm text-gray-400 mt-1 leading-relaxed">{t.success_body}</p>
              </div>
            </div>
            {Object.keys(changes).length > 0 && (
              <div className="bg-[#0F0F0F] border border-gray-800 rounded-lg p-4 my-5">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-3">
                  Recap modifiche
                </p>
                <div className="space-y-2">
                  {Object.entries(changes).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 font-mono">{k}</span>
                      <span className="text-white font-mono ml-3">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {Object.keys(changes).length === 0 && (
              <p className="text-xs text-gray-500 italic my-4">{t.no_changes_msg}</p>
            )}
            <button
              onClick={() => navigate(`/wallet/${customerId}`)}
              className="w-full bg-[#1A1A1A] hover:bg-[#252525] border border-gray-700 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="self-update-back-btn"
            >
              <ArrowLeft size={16} />
              {t.back_to_wallet}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
